import { type AxiosError, type AxiosResponse } from 'axios';
import keyBy from 'lodash-es/keyBy';
import { call, put, takeLatest } from 'redux-saga/effects';

import * as carrierAccountsActions from '~/actions/carrier-accounts.actions';
import { ActionKeys } from '~/actions/constants';
import { createEndpointCarriers } from '~/constants/carriers';
import { SET_FIRST_CARRIER } from '~/constants/gtm';
import { getUserCarrierAccounts } from '~/selectors/carrier-accounts.selectors';
import * as carrierAccountsService from '~/services/carrier-accounts.service';
import { sendGTMEvent } from '~/services/gtm.service';
import * as Carrier from '~/types/Carrier';
import * as CarrierAccount from '~/types/CarrierAccount';
import * as CarrierAccountRegStatus from '~/types/CarrierAccountRegStatus';

import { selectState } from '..';
import { fetchServiceLevelsByAccount } from '../providers/providers.saga';

function* fetchCarrierAccounts(
  payload: ReturnType<typeof carrierAccountsActions.fetchCarrierAccounts>,
) {
  try {
    const response: AxiosResponse<{ results: CarrierAccount.JSONCarrierAccount[] }> = yield call(
      carrierAccountsService.getCarrierAccounts,
      payload.payload,
    );
    const carrierAccountsResponse = response.data.results.map(CarrierAccount.fromJSON);
    const accountCarriers = carrierAccountsResponse
      .filter((account) => !!account.carrier)
      .map((account) => account.carrier?.providerToken);

    // Fetch and append extra carrier fields from adapter_provider to each account
    const carrierProvidersResponse: AxiosResponse<{
      results: { carriers: Carrier.JSONCarrier[] };
    }> = yield call(carrierAccountsService.getCarrierProviders, accountCarriers);

    const carriersExtraFields = carrierProvidersResponse.data.results.carriers.map(
      Carrier.extraFieldsFromJSON,
    );
    const carriersMap = keyBy(carriersExtraFields, 'providerToken');

    // Join extra fields from carrier provider to carrier accounts
    const carrierAccounts = carrierAccountsResponse
      .filter((account) => account.carrier)
      .map((account) => ({
        ...account,
        carrier: {
          ...account.carrier,
          ...carriersMap[account.carrier.providerToken],
        },
      }));

    // Parse response to CarrierAccount objects then filter invalid carriers (currently don't support ShippoCommon) - Vincent
    const parsedCarrierAccounts: carrierAccountsActions.FetchCarrierAccountsSuccessPayload = {
      carrierAccounts,
    };

    yield put(carrierAccountsActions.fetchCarrierAccountsSuccess(parsedCarrierAccounts));
  } catch (err) {
    yield put(
      carrierAccountsActions.fetchCarrierAccountsError({ error: (err as AxiosError).message }),
    );
  }
}

function* createCarrierAccount(
  action: ReturnType<typeof carrierAccountsActions.createCarrierAccount>,
) {
  try {
    let response: AxiosResponse<CarrierAccount.JSONCarrierAccount>;

    // Currently the /carrier_accounts/register/new endpoint only supports UPS Master
    // The other carrer that supports Create is Canada Post, but has not been migrated to this endpoint
    if (
      createEndpointCarriers.includes(action.payload.carrierAccount.carrier.id) ||
      action.payload.carrierAccount.parameters?.isMasterAccount
    ) {
      const jsonCreateCarrierAccount: CarrierAccount.JSONCreateCarrierAccount =
        CarrierAccount.toCreateJSON(action.payload.carrierAccount);
      response = yield call(carrierAccountsService.createCarrierAccount, jsonCreateCarrierAccount);
    } else {
      // Else not supported, so use legacy endpoint /carrier_accounts to create account
      const jsonCarrierAccount: CarrierAccount.JSONCarrierAccount = CarrierAccount.toJSON(
        action.payload.carrierAccount,
      );
      response = yield call(carrierAccountsService.connectCarrierAccount, jsonCarrierAccount);
    }

    const carrierAccount = CarrierAccount.fromJSON(response.data)!;

    yield put(carrierAccountsActions.createCarrierAccountSuccess({ carrierAccount }));
  } catch (err) {
    let errorMessage;
    if (err && (err as AxiosError).response) {
      const responseData = (err as AxiosError).response?.data;
      errorMessage =
        typeof responseData === 'object'
          ? Object.values(responseData).join('\n')
          : Array.isArray(responseData)
          ? responseData.join('\n')
          : responseData;
    } else {
      errorMessage = 'An error occurred. Please contact support.';
    }

    yield put(carrierAccountsActions.createCarrierAccountError({ error: errorMessage }));
  }
}

function* connectCarrierAccount(
  action: ReturnType<typeof carrierAccountsActions.connectCarrierAccount>,
) {
  try {
    const jsonCarrierAccount: CarrierAccount.JSONCarrierAccount = CarrierAccount.toJSON(
      action.payload.carrierAccount,
    );

    const carriers: readonly CarrierAccount.CarrierAccount[] =
      yield selectState<readonly CarrierAccount.CarrierAccount[]>(getUserCarrierAccounts);
    const response: AxiosResponse<CarrierAccount.JSONCarrierAccount> = yield call(
      carrierAccountsService.connectCarrierAccount,
      jsonCarrierAccount,
    );

    const carrierAccount = CarrierAccount.fromJSON(response.data)!;

    yield put(carrierAccountsActions.connectCarrierAccountSuccess({ carrierAccount }));

    // Triggering GTM and Heap for first carrier connect success
    if (carriers.length === 0) {
      yield call(sendGTMEvent, {
        eventName: SET_FIRST_CARRIER,
      });
    }
  } catch (err) {
    let errorMessage;
    if ((err as AxiosError).response) {
      const responseData = (err as AxiosError).response?.data;
      errorMessage =
        typeof responseData === 'object'
          ? Object.values(responseData).join('\n')
          : Array.isArray(responseData)
          ? responseData.join('\n')
          : responseData;
    } else {
      errorMessage = 'An error occurred. Please contact support.';
    }

    yield put(carrierAccountsActions.connectCarrierAccountError({ error: errorMessage }));
  }
}

function* editCarrierAccount(action: ReturnType<typeof carrierAccountsActions.editCarrierAccount>) {
  try {
    const jsonCarrierAccount: CarrierAccount.JSONCarrierAccount = CarrierAccount.toJSON(
      action.payload.carrierAccount,
    );

    const response: AxiosResponse<CarrierAccount.JSONCarrierAccount> = yield call(
      carrierAccountsService.editCarrierAccount,
      jsonCarrierAccount,
      action.payload.id,
    );
    const carrierAccount = CarrierAccount.fromJSON(response.data)!;

    yield put(carrierAccountsActions.editCarrierAccountSuccess({ carrierAccount }));
  } catch (err) {
    let errorMessage;
    if ((err as AxiosError).response) {
      const responseData = (err as AxiosError).response?.data;
      errorMessage =
        typeof responseData === 'object'
          ? Object.values(responseData).join('\n')
          : Array.isArray(responseData)
          ? responseData.join('\n')
          : responseData;
    } else {
      errorMessage = 'An error occurred. Please contact support.';
    }

    yield put(carrierAccountsActions.editCarrierAccountError({ error: errorMessage }));
  }
}

function* editCarrierAccountServiceLevels(
  action: ReturnType<typeof carrierAccountsActions.editCarrierAccountServiceLevels>,
) {
  try {
    const response: AxiosResponse<unknown> = yield call(
      carrierAccountsService.editCarrierAccountServiceLevels,
      action.payload.serviceLevelsPayload!,
      action.payload.id,
    );

    yield call(fetchServiceLevelsByAccount);
    yield put(
      carrierAccountsActions.editCarrierAccountServiceLevelsSuccess({ status: response.status }),
    );

    // after successful Service Levels update, call editCarrierAccount() to update Account options
    yield put(
      carrierAccountsActions.editCarrierAccount({
        carrierAccount: action.payload.carrierAccount!,
        id: action.payload.id,
      }),
    );
  } catch (err) {
    let errors;

    if ((err as AxiosError).response) {
      const responseData = (err as AxiosError).response?.data;

      errors = responseData.errors || [];

      // if legacy responseData.error (string), wrap error in Array bracket
      if (responseData.error) errors = [responseData.error];
    }

    yield put(carrierAccountsActions.editCarrierAccountServiceLevelsError(errors));
  }
}

export function* fetchCarrierAccountRegStatuses() {
  try {
    const response: AxiosResponse<{
      results: CarrierAccountRegStatus.JSONCarrierAccountRegStatus[];
    }> = yield call(carrierAccountsService.fetchCarrierAccountRegStatuses);

    const carrierAccountRegStatuses = response.data.results.map((regStatus) =>
      CarrierAccountRegStatus.fromJSON(regStatus),
    );

    yield put(
      carrierAccountsActions.fetchCarrierAccountRegStatusesSuccess({ carrierAccountRegStatuses }),
    );
  } catch (err) {
    yield put(
      carrierAccountsActions.fetchCarrierAccountRegStatusesError({
        error: (err as AxiosError).message,
      }),
    );
  }
}

export default [
  takeLatest(ActionKeys.FETCH_CARRIER_ACCOUNTS, fetchCarrierAccounts),
  takeLatest(ActionKeys.CONNECT_CARRIER_ACCOUNT, connectCarrierAccount),
  takeLatest(ActionKeys.EDIT_CARRIER_ACCOUNT, editCarrierAccount),
  takeLatest(ActionKeys.EDIT_CARRIER_ACCOUNT_SERVICE_LEVELS, editCarrierAccountServiceLevels),
  takeLatest(ActionKeys.CREATE_CARRIER_ACCOUNT, createCarrierAccount),
  takeLatest(
    [
      ActionKeys.FETCH_CARRIER_ACCOUNT_REG_STATUSES,
      ActionKeys.CREATE_CARRIER_ACCOUNT_SUCCESS,
      ActionKeys.CREATE_CARRIER_ACCOUNT_ERROR,
    ],
    fetchCarrierAccountRegStatuses,
  ),
];
